import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative, IDimensionDefinition } from '@feathr/blackbox';
import { UniversalSelect } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import type { TChannel } from '../types';
import AdDimensionOption from './AdDimensionOption';
import AdDimensionSingleValue from './AdDimensionSingleValue';
import { useAdDimensionsSelect } from './AdDimensionsSelect.hooks';
import type { IAdDimensionOption } from './AdDimensionsSelect.types';

interface IBaseProps {
  channel?: TChannel;
  dimensions?: Record<string, IDimensionDefinition>;
  helpText?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  label?: string;
  menuPortalTarget?: HTMLElement;
  name?: string;
  onClear?: () => void;
  placeholder?: string;
  wrapperClassName?: string;
}

interface ICreativeProps extends IBaseProps {
  creative: DisplayCreative;
  onChange?: never;
  value?: never;
}

interface IControlledProps extends IBaseProps {
  creative?: never;
  onChange: (spec: string) => void;
  value?: string;
}

type TAdDimensionsSelectProps = ICreativeProps | IControlledProps;

function AdDimensionsSelect({
  channel,
  creative,
  dimensions,
  helpText,
  isClearable = false,
  isSearchable = true,
  label,
  menuPortalTarget,
  name,
  onChange,
  onClear,
  placeholder,
  value,
  wrapperClassName,
}: Readonly<TAdDimensionsSelectProps>): JSX.Element {
  const { t } = useTranslation();

  const { dimensionOptions, handleSelectDimensions, selectedValue } = useAdDimensionsSelect({
    channel,
    creative,
    dimensions,
    onChange,
    value,
  });

  return (
    <UniversalSelect<IAdDimensionOption>
      components={{ Option: AdDimensionOption, SingleValue: AdDimensionSingleValue }}
      controlPrefix={isSearchable ? getIconForAction('search') : undefined}
      helpText={helpText}
      isClearable={isClearable}
      isSearchable={isSearchable}
      label={label}
      menuPlacement={'top'}
      menuPortalTarget={menuPortalTarget}
      name={name ?? 'ad-dimensions'}
      onClear={onClear}
      onSelectSingle={handleSelectDimensions}
      options={dimensionOptions}
      placeholder={placeholder ?? t('Type')}
      value={selectedValue}
      wrapperClassName={wrapperClassName}
    />
  );
}

export default observer(AdDimensionsSelect);
